// Procedural Deep Sea habitat: water gradient, light shafts, layered dunes, segmented kelp,
// rising bubbles, a caustic-light shader and marine snow.
import { Container, Graphics, GraphicsContext } from "pixi.js";
import { Rng } from "../lib/rng";
import { INK, OKABE_ITO, mix } from "../palette";
import { CausticFilter } from "./caustics";
import { AmbienceFollower, clamp01, nightness, smoothK } from "./common";
import { ParticlePool, verticalGradient } from "./draw";
import { createNoise1D, fbm1D, type Noise1D } from "./noise";
import type { AmbienceInput, Habitat, HabitatOptions, HabitatVariant } from "./types";

interface WaterPalette {
  top: number;
  mid: number;
  bottom: number;
  sand: number;
  caustic: number;
  causticLevel: number;
}

const WATER: Record<HabitatVariant["timeOfDay"], WaterPalette> = {
  dawn: { top: 0x3a6f95, mid: 0x123a5c, bottom: 0x050f22, sand: 0x6e6050, caustic: 0xffd9b0, causticLevel: 0.4 },
  day: { top: 0x1f86b8, mid: 0x0b4a74, bottom: 0x03142a, sand: 0x7a6a50, caustic: 0xc8f2ff, causticLevel: 0.55 },
  dusk: { top: 0x2c4a7a, mid: 0x10284f, bottom: 0x040a1c, sand: 0x5e5048, caustic: 0xffc6a0, causticLevel: 0.3 },
  night: { top: 0x0c2a48, mid: 0x061830, bottom: 0x01050d, sand: 0x3a3a40, caustic: 0x9fd8ff, causticLevel: 0.12 },
};

const KELP_COLS = [0x3f7f3a, 0x5a8f2e, 0x2e6b4f, 0x6b8f3a];
const CORAL_COLS = [OKABE_ITO.reddishPurple, OKABE_ITO.orange, OKABE_ITO.vermillion, OKABE_ITO.yellow];

interface Kelp {
  root: Container;
  segs: Container[];
  phase: number;
  speed: number;
}

interface Shaft {
  g: Graphics;
  phase: number;
}

export function createDeepSea(opts: HabitatOptions): Habitat {
  let w = Math.max(1, opts.width);
  let h = Math.max(1, opts.height);
  let variant = { ...opts.variant };
  let reduced = opts.reducedMotion;
  let particles = opts.particles;
  const seed = opts.seed >>> 0;

  const duneNoise = [createNoise1D(seed + 11), createNoise1D(seed + 12), createNoise1D(seed + 13)];
  const groundNoise = createNoise1D(seed + 14);

  const view = new Container();
  view.label = "habitat:deep_sea";

  const water = new Graphics();
  const shaftLayer = new Container();
  shaftLayer.blendMode = "add";
  const duneLayers = [new Container(), new Container()];
  const dunes = duneLayers.map((c) => c.addChild(new Graphics()));
  const kelpBack = new Container();
  duneLayers[1].addChild(kelpBack);
  const ground = new Graphics();
  const kelpFront = new Container();
  const causticRect = new Graphics();
  causticRect.blendMode = "add";
  let causticFilter: CausticFilter | null = null;
  try {
    causticFilter = new CausticFilter();
    causticRect.filters = [causticFilter];
  } catch {
    causticFilter = null; // shader unavailable: scene still renders without caustics
  }
  const murk = new Graphics();

  const bubbleCtx = new GraphicsContext()
    .circle(0, 0, 5)
    .fill({ color: 0xcdeef9, alpha: 0.12 })
    .stroke({ width: 1.6, color: 0xe6f7ff, alpha: 0.8 })
    .circle(-1.8, -1.8, 1.3)
    .fill({ color: 0xffffff, alpha: 0.9 });
  const bubbles = new ParticlePool(bubbleCtx, 110);
  const snowCtx = new GraphicsContext().circle(0, 0, 1.6).fill({ color: 0xe8f0f2, alpha: 0.75 });
  const snow = new ParticlePool(snowCtx, 160);

  view.addChild(
    water,
    shaftLayer,
    ...duneLayers,
    ground,
    kelpFront,
    causticRect,
    bubbles.container,
    snow.container,
    murk,
  );

  const kelps: Kelp[] = [];
  const shafts: Shaft[] = [];
  const amb = new AmbienceFollower();
  let time = 0;
  let causticTime = 0;
  let bubbleAcc = 0;
  let lastPulse = 0;
  const parallax = { tx: 0, ty: 0, x: 0, y: 0 };
  const margin = () => Math.round(w * 0.06);

  function groundYAt(xf: number): number {
    return h * 0.82 + fbm1D(groundNoise, xf * 1.8 + 7.7, 3) * h * 0.03;
  }

  function duneY(noise: Noise1D, x: number, base: number, amp: number, freq: number): number {
    // Dunes: smooth, asymmetric ridges (abs-folded noise reads like sand ripples)
    const n = fbm1D(noise, (x / w) * freq + 5, 3);
    return base + (n - Math.abs(fbm1D(noise, (x / w) * freq * 2.3 + 40, 2)) * 0.4) * amp;
  }

  function makeKelp(rng: Rng, x: number, y: number, scale: number, color: number): Kelp {
    const root = new Container();
    root.position.set(x, y);
    const segCount = rng.int(5, 10);
    const segLen = h * 0.045 * scale;
    const segs: Container[] = [];
    let parent: Container = root;
    for (let i = 0; i < segCount; i++) {
      const seg = new Graphics();
      const wid = (7 - i * 0.35) * scale;
      // stalk segment (rounded) with bold outline
      seg
        .roundRect(-wid / 2, -segLen - 2, wid, segLen + 4, wid / 2)
        .fill(color)
        .stroke({ width: 2.5, color: INK, alpha: 0.75 });
      // blade on alternating sides
      const side = i % 2 === 0 ? 1 : -1;
      const bl = segLen * rng.range(0.9, 1.4);
      const bw = segLen * 0.32;
      seg
        .moveTo(0, -segLen * 0.6)
        .quadraticCurveTo(side * bl * 0.5, -segLen * 0.6 - bw * 1.6, side * bl, -segLen * 0.9 - bw)
        .quadraticCurveTo(side * bl * 0.45, -segLen * 0.6 + bw * 0.4, 0, -segLen * 0.4)
        .closePath()
        .fill(mix(color, 0x9fd36b, 0.25))
        .stroke({ width: 2, color: INK, alpha: 0.6 });
      // a float bulb near the tip, like giant kelp
      if (i === segCount - 1)
        seg
          .circle(0, -segLen - 3, wid * 0.8)
          .fill(mix(color, OKABE_ITO.yellow, 0.3))
          .stroke({ width: 2, color: INK, alpha: 0.7 });
      // each segment hangs off the end of the previous one
      // (wrapped in a Container joint: Pixi v8 only lets Containers have children)
      const joint = new Container();
      joint.addChild(seg);
      joint.position.set(0, i === 0 ? 0 : -segLen);
      parent.addChild(joint);
      segs.push(joint);
      parent = joint;
    }
    return { root, segs, phase: rng.range(0, 6.28), speed: rng.range(0.5, 0.9) };
  }

  function build(): void {
    const rng = new Rng(seed ^ 0x51ed270b);
    const pal = WATER[variant.timeOfDay];
    const night = nightness(variant.timeOfDay);
    const m = margin();

    water.clear();
    verticalGradient(
      water,
      -m,
      -m,
      w + 2 * m,
      h + 2 * m,
      [
        [0, pal.top],
        [0.45, pal.mid],
        [1, pal.bottom],
      ],
      40,
    );
    // surface ripple band
    for (let i = 0; i < 3; i++) {
      const pts: number[] = [];
      for (let x = -m; x <= w + m; x += 12) pts.push(x, h * (0.015 + i * 0.012) + Math.sin(x * 0.03 + i * 2) * 3);
      water.poly(pts, false).stroke({ width: 2, color: 0xe6f7ff, alpha: 0.15 - i * 0.04 });
    }

    // light shafts
    for (const s of shafts) s.g.destroy();
    shafts.length = 0;
    const shaftCount = variant.timeOfDay === "night" ? 2 : 5;
    for (let i = 0; i < shaftCount; i++) {
      const g = new Graphics();
      const topW = w * rng.range(0.04, 0.09);
      const botW = topW * rng.range(2.5, 4);
      const len = h * rng.range(0.6, 0.9);
      g.poly([-topW / 2, 0, topW / 2, 0, botW / 2, len, -botW / 2, len], true).fill({
        color: pal.caustic,
        alpha: 0.05 * (1 - night * 0.6),
      });
      g.poly([-topW / 4, 0, topW / 4, 0, botW / 4, len * 0.8, -botW / 4, len * 0.8], true).fill({
        color: pal.caustic,
        alpha: 0.04 * (1 - night * 0.6),
      });
      g.position.set(rng.range(0, w), -10);
      g.rotation = rng.range(-0.35, 0.1);
      shaftLayer.addChild(g);
      shafts.push({ g, phase: rng.range(0, 6.28) });
    }

    // Dunes: far, mid (ground is the near layer)
    const duneCfg = [
      { base: 0.62, amp: 0.08, freq: 1.6, fade: 0.72 },
      { base: 0.72, amp: 0.06, freq: 2.6, fade: 0.45 },
    ];
    const step = Math.max(6, w / 160);
    duneCfg.forEach((c, i) => {
      const g = dunes[i];
      g.clear();
      const col = mix(pal.sand, pal.mid, c.fade);
      const pts: number[] = [];
      for (let x = -m; x <= w + m + step; x += step) pts.push(x, duneY(duneNoise[i], x, h * c.base, h * c.amp, c.freq));
      const top = pts.slice();
      pts.push(w + m + step, h + m, -m, h + m);
      g.poly(pts, true).fill(col);
      g.poly(top, false).stroke({ width: 3, color: INK, alpha: 0.25 + i * 0.1, join: "round" });
      // rock arches / boulders on the far dune
      if (i === 0) {
        for (let k = 0; k < 5; k++) {
          const rx = rng.range(-m, w + m);
          const ry = duneY(duneNoise[0], rx, h * c.base, h * c.amp, c.freq) + 4;
          const rs = h * rng.range(0.03, 0.07);
          g.ellipse(rx, ry - rs * 0.4, rs * rng.range(1, 1.8), rs).fill(mix(col, INK, 0.25));
        }
      }
    });

    // Ground (near dune) with shells, starfish and coral
    ground.clear();
    const gcol = mix(pal.sand, pal.mid, 0.2);
    const gpts: number[] = [];
    for (let x = -m; x <= w + m + step; x += step) gpts.push(x, groundYAt(x / w));
    const gtop = gpts.slice();
    gpts.push(w + m + step, h + m, -m, h + m);
    ground.poly(gpts, true).fill(gcol);
    for (let i = 0; i < 40; i++) {
      // sand ripples
      const x = rng.range(-m, w + m);
      const y = groundYAt(x / w) + rng.range(12, h * 0.16);
      const rw = rng.range(14, 34);
      ground
        .moveTo(x - rw, y)
        .quadraticCurveTo(x, y - 4, x + rw, y)
        .stroke({ width: 2, color: mix(gcol, INK, 0.25), alpha: 0.5 });
    }
    for (let i = 0; i < 5; i++) {
      const x = rng.range(0.03, 0.97) * w;
      const y = groundYAt(x / w) + rng.range(10, 30);
      const r = rng.range(5, 9);
      ground
        .star(x, y, 5, r, r * 0.45, rng.range(0, 6.28))
        .fill(mix(OKABE_ITO.orange, pal.mid, 0.25 + night * 0.4))
        .stroke({ width: 2, color: INK, alpha: 0.7 });
    }
    for (let i = 0; i < 4; i++) {
      // coral fans: little branching strokes
      const x = rng.range(0, w);
      const y = groundYAt(x / w) + 4;
      const col = mix(rng.pick(CORAL_COLS), pal.mid, 0.25 + night * 0.4);
      const branches = rng.int(3, 5);
      const ch = h * rng.range(0.04, 0.07);
      for (let b = 0; b < branches; b++) {
        const a = -Math.PI / 2 + (b - (branches - 1) / 2) * 0.35;
        const ex = x + Math.cos(a) * ch;
        const ey = y + Math.sin(a) * ch;
        ground
          .moveTo(x, y)
          .quadraticCurveTo(x + Math.cos(a) * ch * 0.3, y - ch * 0.5, ex, ey)
          .stroke({ width: 6, color: INK, alpha: 0.75, cap: "round" });
        ground
          .moveTo(x, y)
          .quadraticCurveTo(x + Math.cos(a) * ch * 0.3, y - ch * 0.5, ex, ey)
          .stroke({ width: 3.5, color: col, cap: "round" });
        ground.circle(ex, ey, 3.5).fill(col).stroke({ width: 1.5, color: INK, alpha: 0.7 });
      }
    }
    ground.poly(gtop, false).stroke({ width: 4, color: INK, alpha: 0.5, join: "round" });

    // Kelp: a forest on the mid dune, a few tall ones framing the edges in front
    for (const k of kelps) k.root.destroy({ children: true });
    kelps.length = 0;
    const back = Math.max(4, Math.round(w / 110));
    for (let i = 0; i < back; i++) {
      const x = ((i + rng.range(0.1, 0.9)) / back) * w;
      const y = duneY(duneNoise[1], x, h * duneCfg[1].base, h * duneCfg[1].amp, duneCfg[1].freq) + 6;
      const k = makeKelp(rng, x, y, rng.range(0.7, 1.1), mix(rng.pick(KELP_COLS), pal.mid, 0.35 + night * 0.3));
      kelpBack.addChild(k.root);
      kelps.push(k);
    }
    for (let i = 0; i < 4; i++) {
      const left = i % 2 === 0;
      const x = left ? rng.range(0.0, 0.07) * w : rng.range(0.93, 1.0) * w;
      const k = makeKelp(rng, x, h + 4, rng.range(1.2, 1.6), mix(rng.pick(KELP_COLS), INK, 0.1 + night * 0.4));
      kelpFront.addChild(k.root);
      kelps.push(k);
    }

    causticRect.clear();
    causticRect.rect(0, 0, w, h).fill({ color: 0xffffff, alpha: 1 });
    if (causticFilter) {
      causticFilter.setColor(pal.caustic);
      causticFilter.setSize(w, h);
    }
    causticRect.visible = !!causticFilter;

    murk.clear();
    if (variant.weather === "mist") {
      murk.rect(-m, -m, w + 2 * m, h + 2 * m).fill({ color: 0x0b3a3a, alpha: 0.28 });
      for (let i = 0; i < 6; i++)
        murk
          .ellipse(rng.range(0, w), rng.range(h * 0.3, h * 0.8), w * rng.range(0.2, 0.4), h * rng.range(0.06, 0.12))
          .fill({ color: 0x2a6b6b, alpha: 0.12 });
    }
    bubbles.clear();
    snow.clear();
    applyStatic();
  }

  function applyStatic(): void {
    for (const k of kelps) for (const s of k.segs) s.rotation = 0;
    for (const s of shafts) s.g.alpha = 1;
  }

  function spawnBubble(fromKelp: boolean): void {
    const p = bubbles.spawn();
    if (!p) return;
    if (fromKelp && kelps.length > 0) {
      const k = kelps[Math.floor(Math.random() * kelps.length)];
      const gp = k.root.parent === kelpFront ? k.root.position : { x: k.root.x + duneLayers[1].x, y: k.root.y };
      p.x = gp.x + (Math.random() - 0.5) * 10;
      p.y = Math.min(gp.y, h) - Math.random() * 20;
    } else {
      const xf = Math.random();
      p.x = xf * w;
      p.y = groundYAt(xf);
    }
    p.vy = -(35 + Math.random() * 55);
    p.vx = 0;
    p.size = 0.5 + Math.random() * 0.9;
    p.phase = Math.random() * 6.28;
  }

  function updateBubbles(dt: number): void {
    const rate = 2.5 + amb.level * 22; // bubbles per second
    bubbleAcc += rate * dt;
    while (bubbleAcc >= 1) {
      bubbleAcc -= 1;
      spawnBubble(Math.random() < 0.6);
    }
    // burst on strong beats
    if (amb.pulse > lastPulse + 0.25) {
      const n = Math.round(2 + amb.peak * 6);
      for (let i = 0; i < n; i++) spawnBubble(false);
    }
    lastPulse = amb.pulse;
    for (const p of bubbles.items) {
      if (!p.active) continue;
      p.age += dt;
      p.y += p.vy * dt * (1 + p.age * 0.15);
      const wob = Math.sin(time * 3 + p.phase) * 8 * p.size;
      if (p.y < -20) {
        bubbles.kill(p);
        continue;
      }
      const s = p.size * (1 + Math.min(0.6, p.age * 0.08));
      p.g.position.set(p.x + wob, p.y);
      p.g.scale.set(s * (1 + 0.08 * Math.sin(time * 9 + p.phase)), s * (1 - 0.08 * Math.sin(time * 9 + p.phase)));
      p.g.alpha = clamp01(p.age * 3);
    }
  }

  function updateSnow(dt: number): void {
    const dense = variant.weather === "snow";
    const current = variant.weather === "rain";
    // A little marine snow is always present in the deep; "snow" weather makes it dense.
    const target = dense ? snow.capacity : current ? 110 : 25;
    let active = snow.activeCount;
    let budget = dense || current ? 6 : 1;
    while (active < target && budget-- > 0) {
      const p = snow.spawn();
      if (!p) break;
      active++;
      p.x = current ? -10 : Math.random() * w;
      p.y = current ? Math.random() * h * 0.85 : -10;
      p.vx = current ? 60 + Math.random() * 80 : (Math.random() - 0.5) * 6;
      p.vy = current ? (Math.random() - 0.5) * 8 : 8 + Math.random() * 14;
      p.size = 0.5 + Math.random() * 1.1;
      p.phase = Math.random() * 6.28;
    }
    for (const p of snow.items) {
      if (!p.active) continue;
      p.x += (p.vx + Math.sin(time * 0.7 + p.phase) * 6) * dt;
      p.y += p.vy * dt;
      if (p.y > h + 10 || p.x > w + 20 || p.x < -20) {
        snow.kill(p);
        continue;
      }
      p.g.position.set(p.x, p.y);
      p.g.scale.set(current ? p.size * 1.4 : p.size, p.size);
    }
  }

  build();

  return {
    view,
    resize(nw, nh) {
      w = Math.max(1, nw);
      h = Math.max(1, nh);
      build();
    },
    setVariant(v) {
      if (v.timeOfDay === variant.timeOfDay && v.weather === variant.weather) return;
      variant = { ...v };
      build();
    },
    setMotion(r, p) {
      reduced = r;
      particles = p;
      if (!particles) {
        bubbles.clear();
        snow.clear();
      }
      if (reduced) applyStatic();
    },
    update(dt, a: AmbienceInput) {
      dt = Math.min(0.1, Math.max(0, dt));
      time += dt;
      amb.update(dt, a);

      const k = smoothK(dt, 0.25);
      parallax.x += ((reduced ? 0 : parallax.tx) - parallax.x) * k;
      parallax.y += ((reduced ? 0 : parallax.ty) - parallax.y) * k;
      const m = margin();
      shaftLayer.position.set(-parallax.x * m * 0.1, 0);
      duneLayers.forEach((c, i) =>
        c.position.set(-parallax.x * m * (0.25 + i * 0.25), -parallax.y * m * 0.04 * (i + 1)),
      );
      kelpFront.position.set(-parallax.x * m * 0.9, 0);

      const pal = WATER[variant.timeOfDay];
      if (!reduced) {
        const sway = 0.5 + amb.level * 1.3 + amb.pulse * 0.5;
        for (const kp of kelps) {
          // travelling wave up the stalk: each segment lags the one below
          for (let i = 0; i < kp.segs.length; i++) {
            kp.segs[i].rotation = Math.sin(time * kp.speed * 1.6 - i * 0.55 + kp.phase) * (0.05 + i * 0.012) * sway;
          }
        }
        for (const s of shafts) s.g.alpha = 0.6 + 0.4 * Math.sin(time * 0.4 + s.phase) + amb.pulse * 0.5;
        causticTime += dt * (0.8 + amb.level * 0.8);
      } else {
        causticTime += dt * 0.1;
      }
      if (causticFilter) {
        causticFilter.time = causticTime;
        const pulse = reduced ? 0 : amb.pulse;
        const murkK = variant.weather === "mist" ? 0.5 : 1;
        causticFilter.intensity = pal.causticLevel * murkK * (0.55 + amb.level * 0.6 + pulse * 0.7);
      }
      if (particles) {
        updateBubbles(dt);
        updateSnow(dt);
      }
    },
    setParallax(x, y) {
      parallax.tx = Math.max(-1, Math.min(1, x));
      parallax.ty = Math.max(-1, Math.min(1, y));
    },
    groundY(xf) {
      return groundYAt(xf);
    },
    destroy() {
      bubbles.destroy();
      snow.destroy();
      view.destroy({ children: true });
      causticFilter?.destroy();
    },
  };
}
